/* The w1-04 eligibility engine (flight-delay-monitoring app/Intake/Eligibility), ported to
   TypeScript for the fake API only. The real verdict is always the API's; this port is held
   to the engine's own fixture table (src/test/fixtures/eligibility-cases.json) so the fake
   answers like the API would. Pure: no clock, no I/O. */
import type { Eligibility, Money, ReasonCode, Regime } from '../contract';

export type Disruption = 'delayed' | 'cancelled' | 'denied_boarding' | 'missed_connection';

export interface EligibilityInput {
  originCountry: string;
  destinationCountry: string;
  carrierCountry?: string | null;
  distanceKm: number;
  disruption?: Disruption | null;
  arrivalDelayMin?: number | null;
  selfReportedDelay?: '3h_or_more' | '3h_to_4h' | '4h_or_more' | 'under_3h' | 'never_arrived' | null;
  cancellationNotice?: 'less_than_14_days' | '14_days_or_more' | 'not_told' | null;
  /** Alternative to `cancellationNotice`: < 14 → less than 14 days. */
  noticeDays?: number | null;
  volunteered?: boolean | null;
  cancellationReason?: string | null;
  /** Q44: staff's review of a borderline arrival delay (`over_3h` counts as 3 h; `under_3h`: the gate time stands). */
  arrivalDelayReview?: 'over_3h' | 'under_3h' | null;
}

const EU_MEMBER_STATES = [
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU',
  'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]; // prettier-ignore
const EU_TERRITORIES_WITH_OWN_CODE = ['GP', 'GF', 'MQ', 'RE', 'YT', 'MF', 'AX'];
const EC261_AREA = new Set([...EU_MEMBER_STATES, ...EU_TERRITORIES_WITH_OWN_CODE, 'IS', 'LI', 'NO', 'CH']);
const UK = new Set(['GB']);
const UK261_CARRIERS = new Set(['GB', ...EU_MEMBER_STATES, ...EU_TERRITORIES_WITH_OWN_CODE]);
const EXTRAORDINARY = new Set([
  'weather',
  'air_traffic_control',
  'security',
  'external_strike',
  'manufacturing_defect',
]);

export const DELAY_THRESHOLD_MIN = 180;
/** Q44: the API's default first minute of the borderline band (config intake.eligibility.borderline_from_min). */
export const BORDERLINE_FROM_MIN = 170;
const LONG_HAUL_REDUCTION_MAX_MIN = 240;

export type Tier = 'short' | 'medium' | 'long';

const AMOUNTS: Record<'EC261' | 'UK261', Record<Tier, Money>> = {
  EC261: {
    short: { amount: 250, currency: 'EUR' },
    medium: { amount: 400, currency: 'EUR' },
    long: { amount: 600, currency: 'EUR' },
  },
  UK261: {
    short: { amount: 220, currency: 'GBP' },
    medium: { amount: 350, currency: 'GBP' },
    long: { amount: 520, currency: 'GBP' },
  },
};

/** Which regime governs the journey; null when it depends on an unknown carrier. */
export function regimeFor(origin: string, destination: string, carrier: string | null): Regime | null {
  if (EC261_AREA.has(origin)) return 'EC261';
  if (UK.has(origin)) return 'UK261';
  const arrivesEc = EC261_AREA.has(destination);
  const arrivesUk = UK.has(destination);
  if (!arrivesEc && !arrivesUk) return 'none';
  if (carrier === null) return null;
  if (arrivesEc && EC261_AREA.has(carrier)) return 'EC261';
  if (arrivesUk && UK261_CARRIERS.has(carrier)) return 'UK261';
  return 'none';
}

function tierFor(distanceKm: number, intra: boolean): Tier {
  if (distanceKm <= 1500) return 'short';
  if (intra || distanceKm <= 3500) return 'medium';
  return 'long';
}

function isIntra(regime: 'EC261' | 'UK261', origin: string, destination: string) {
  const area = regime === 'EC261' ? EC261_AREA : UK;
  return area.has(origin) && area.has(destination);
}

/** The Art. 7(1) band of a journey under a regime (the quote's `band`). */
export function bandFor(
  regime: 'EC261' | 'UK261',
  input: Pick<EligibilityInput, 'originCountry' | 'destinationCountry' | 'distanceKm'>,
): Tier {
  return tierFor(input.distanceKm, isIntra(regime, input.originCountry, input.destinationCountry));
}

/** Over 3,500 km, but both ends in the regime's own area: paid the middle band (Q4). */
export const intraAreaCapped = (distanceKm: number, intra: boolean) => intra && distanceKm > 3500;

/** The Art. 7(1) amount for a journey under a regime (no reduction). */
export function amountFor(
  regime: 'EC261' | 'UK261',
  input: Pick<EligibilityInput, 'originCountry' | 'destinationCountry' | 'distanceKm'>,
): Money {
  return AMOUNTS[regime][
    tierFor(input.distanceKm, isIntra(regime, input.originCountry, input.destinationCountry))
  ];
}

const result = (
  regime: Regime,
  amountPerPassenger: Money | null,
  verdict: Eligibility['verdict'],
  confidence: Eligibility['confidence'],
  reasons: ReasonCode[],
  reduced = false,
): Eligibility => ({ regime, amountPerPassenger, reduced, verdict, confidence, reasons });

/**
 * `EligibilityEngine::evaluate`. `forceRegime` is the fake's dev switch (the prototype's EU/UK
 * jurisdiction toggle): it replaces the scope decision for an in-scope journey.
 */
export function evaluate(input: EligibilityInput, forceRegime?: 'EC261' | 'UK261'): Eligibility {
  let regime = regimeFor(input.originCountry, input.destinationCountry, input.carrierCountry ?? null);
  if (forceRegime && regime !== 'none') regime = forceRegime;
  if (regime === null) return result('none', null, 'pending', 'self_report', ['carrier_unknown']);
  if (regime === 'none') return result('none', null, 'ineligible', 'data', ['outside_scope']);

  const intra = isIntra(regime, input.originCountry, input.destinationCountry);
  const r = byDisruption(input, regime, tierFor(input.distanceKm, intra));
  /* Q4: a figure the intra-area cap lowered says so. */
  if (r.amountPerPassenger && intraAreaCapped(input.distanceKm, intra))
    return { ...r, reasons: [...r.reasons, 'intra_community_cap'] };
  return r;
}

/** Q6 (`EligibilityEngine::evaluateJourney`): end to end first, then the disrupted leg alone. */
export function evaluateJourney(
  journey: EligibilityInput,
  leg: EligibilityInput | null,
  forceRegime?: 'EC261' | 'UK261',
): { journey: Eligibility; legAlone: Eligibility | null } {
  const j = evaluate(journey, forceRegime);
  if (!leg || j.verdict !== 'ineligible' || !j.reasons.includes('outside_scope'))
    return { journey: j, legAlone: null };
  const alone = evaluate(leg, forceRegime);
  const claimable = alone.regime !== 'none' && (alone.verdict === 'eligible' || alone.verdict === 'pending');
  return { journey: j, legAlone: claimable ? alone : null };
}

function byDisruption(input: EligibilityInput, regime: 'EC261' | 'UK261', tier: Tier): Eligibility {
  const amount = AMOUNTS[regime][tier];

  switch (input.disruption ?? null) {
    case null:
      return result(regime, amount, 'pending', 'self_report', ['flight_status_pending']);
    case 'delayed':
    case 'missed_connection': {
      let minutes = input.arrivalDelayMin ?? null;
      const reported = input.selfReportedDelay ?? null;
      if (minutes !== null) {
        /* Q44: just under 3 h at the gate may be over 3 h at the door: pending until staff review it. */
        if (minutes >= BORDERLINE_FROM_MIN && minutes < DELAY_THRESHOLD_MIN) {
          const review = input.arrivalDelayReview ?? null;
          if (review === null)
            return tier === 'long'
              ? result(
                  regime,
                  { amount: Math.floor(amount.amount / 2), currency: amount.currency },
                  'pending',
                  'data',
                  ['arrival_delay_borderline', 'long_haul_delay_3_to_4h'],
                  true,
                )
              : result(regime, amount, 'pending', 'data', ['arrival_delay_borderline']);
          if (review === 'over_3h') minutes = DELAY_THRESHOLD_MIN;
        }
        if (minutes < DELAY_THRESHOLD_MIN) {
          const reasons: ReasonCode[] = ['arrival_delay_under_3h'];
          if (reported && reported !== 'under_3h') reasons.push('reported_delay_conflicts_with_data');
          return result(regime, null, 'not_now', 'data', reasons);
        }
        if (tier === 'long' && minutes <= LONG_HAUL_REDUCTION_MAX_MIN) {
          const half = { amount: Math.floor(amount.amount / 2), currency: amount.currency };
          return result(
            regime,
            half,
            'eligible',
            'data',
            ['arrival_delay_3h_or_more', 'long_haul_delay_3_to_4h'],
            true,
          );
        }
        return result(regime, amount, 'eligible', 'data', ['arrival_delay_3h_or_more']);
      }
      /* Q39: S6 splits "3 hours or more" on long haul; 3–4 h pays half. */
      if (reported === '3h_to_4h' && tier === 'long')
        return result(
          regime,
          { amount: Math.floor(amount.amount / 2), currency: amount.currency },
          'eligible',
          'self_report',
          ['arrival_delay_3h_or_more', 'long_haul_delay_3_to_4h'],
          true,
        );
      switch (reported) {
        case '3h_or_more':
        case '3h_to_4h':
        case '4h_or_more':
          return result(regime, amount, 'eligible', 'self_report', ['arrival_delay_3h_or_more']);
        case 'never_arrived':
          return result(regime, amount, 'eligible', 'self_report', ['never_arrived']);
        case 'under_3h':
          return result(regime, null, 'not_now', 'self_report', ['reported_delay_under_3h']);
        default:
          return result(regime, amount, 'pending', 'self_report', ['flight_status_pending']);
      }
    }
    case 'cancelled': {
      const extraordinary = !!input.cancellationReason && EXTRAORDINARY.has(input.cancellationReason);
      const notice =
        input.cancellationNotice ??
        (input.noticeDays !== null && input.noticeDays !== undefined
          ? input.noticeDays < 14
            ? 'less_than_14_days'
            : '14_days_or_more'
          : null);
      if (notice === '14_days_or_more') {
        const reasons: ReasonCode[] = ['cancelled_14_days_or_more_notice'];
        if (extraordinary) reasons.push('extraordinary_circumstances_cited');
        return result(regime, null, 'not_now', 'self_report', reasons);
      }
      if (extraordinary)
        return result(regime, null, 'not_now', 'data', ['extraordinary_circumstances_cited']);
      if (notice === 'less_than_14_days')
        return result(regime, amount, 'eligible', 'self_report', ['cancelled_under_14_days_notice']);
      if (notice === 'not_told')
        return result(regime, amount, 'eligible', 'self_report', ['cancelled_without_notice']);
      return result(regime, amount, 'pending', 'self_report', ['cancellation_notice_unknown']);
    }
    case 'denied_boarding':
      if (input.volunteered === false)
        return result(regime, amount, 'eligible', 'self_report', ['denied_boarding_involuntary']);
      if (input.volunteered === true)
        return result(regime, null, 'not_now', 'self_report', ['volunteered_denied_boarding']);
      return result(regime, amount, 'pending', 'self_report', ['volunteered_unknown']);
  }
}
